import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  FolderTree, Plus, Upload, FileText, Edit2, Trash2, 
  ChevronRight, ChevronDown, Loader2, X, Save, Check
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { confirmToast } from '@/utils/confirmToast';

export default function FormsLibraryPanel() {
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('categories'); // 'categories' or 'forms'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', parent_id: null });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryForm, setEditCategoryForm] = useState({ name: '', description: '' });
  
  // Form upload
  const [showFormUpload, setShowFormUpload] = useState(false);
  const [uploadingForm, setUploadingForm] = useState(false);
  const [formFile, setFormFile] = useState(null);
  const [formData, setFormData] = useState({ form_name: '', form_code: '', description: '', category_id: '' });
  const [stripXfa, setStripXfa] = useState(true); // Default to true
  const [mappingPreview, setMappingPreview] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchForms();
  }, []);

  useEffect(() => {
    // Filter forms based on selected category
    if (selectedCategory) {
      setFilteredForms(forms.filter(f => f.category_id === selectedCategory));
    } else {
      setFilteredForms(forms);
    }
  }, [selectedCategory, forms]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/forms-library/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/forms-library/forms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  const handleCreateCategory = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/forms-library/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryForm)
      });
      
      if (response.ok) {
        toast.success('Category created');
        setShowCategoryForm(false);
        setCategoryForm({ name: '', description: '', parent_id: null });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editCategoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/forms-library/categories/${editingCategory}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editCategoryForm)
      });
      
      if (response.ok) {
        toast.success('Category updated');
        setEditingCategory(null);
        setEditCategoryForm({ name: '', description: '' });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update category');
      }
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = (categoryId, categoryName) => {
    confirmToast(
      `Delete category "${categoryName}"? This will fail if it has subcategories or forms.`,
      async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(`${API}/forms-library/categories/${categoryId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Category deleted');
            fetchCategories();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete category');
          }
        } catch (error) {
          toast.error('Failed to delete category');
        }
      }
    );
  };

  const handleUploadForm = async () => {
    if (!formFile) {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (!formData.form_name.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    
    if (!formData.form_code.trim()) {
      toast.error('Please enter a form code');
      return;
    }
    
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    setUploadingForm(true);
    const token = localStorage.getItem('token');
    const formDataPayload = new FormData();
    formDataPayload.append('file', formFile);
    formDataPayload.append('form_data', JSON.stringify(formData));
    formDataPayload.append('strip_xfa', stripXfa.toString());
    
    try {
      const response = await fetch(`${API}/forms-library/forms/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataPayload
      });
      
      if (response.ok) {
        const result = await response.json();
        setMappingPreview(result);
        toast.success(`Form uploaded! ${result.mapped_fields} fields auto-mapped`);
        setShowFormUpload(false);
        setFormFile(null);
        setFormData({ form_name: '', form_code: '', description: '', category_id: '' });
        setStripXfa(true); // Reset to default
        fetchForms();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload form');
      }
    } catch (error) {
      toast.error('Failed to upload form');
    } finally {
      setUploadingForm(false);
    }
  };

  const handleDeleteForm = (formId, formName) => {
    confirmToast(
      `Delete form "${formName}"? This action cannot be undone.`,
      async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(`${API}/forms-library/forms/${formId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Form deleted');
            fetchForms();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete form');
          }
        } catch (error) {
          toast.error('Failed to delete form');
        }
      }
    );
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoryTree = (parentId = null, level = 0) => {
    const children = categories.filter(c => c.parent_id === parentId);
    
    return children.map(category => {
      const hasChildren = categories.some(c => c.parent_id === category.category_id);
      const isExpanded = expandedCategories.has(category.category_id);
      const formsCount = forms.filter(f => f.category_id === category.category_id).length;
      
      return (
        <div key={category.category_id} style={{ marginLeft: level * 20 }}>
          <div className={`flex items-center gap-2 p-2 rounded-lg group transition-colors ${
            selectedCategory === category.category_id ? 'bg-violet-100' : 'hover:bg-slate-50'
          }`}>
            {hasChildren && (
              <button onClick={() => toggleCategory(category.category_id)} className="p-1">
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {!hasChildren && <div className="w-6" />}
            
            <FolderTree className={`w-4 h-4 ${selectedCategory === category.category_id ? 'text-violet-600' : 'text-blue-600'}`} />
            <span 
              className="flex-1 font-medium text-sm cursor-pointer hover:text-violet-600"
              onClick={() => setSelectedCategory(selectedCategory === category.category_id ? null : category.category_id)}
            >
              {category.name}
            </span>
            <span className="text-xs text-slate-500">{formsCount} forms</span>
            
            <div className="opacity-0 group-hover:opacity-100 flex gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoryForm({ name: '', description: '', parent_id: category.category_id });
                  setShowCategoryForm(true);
                }}
                className="p-1 hover:bg-blue-100 rounded"
                title="Add subcategory"
              >
                <Plus className="w-3 h-3 text-blue-600" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCategory(category.category_id);
                  setEditCategoryForm({ name: category.name, description: category.description || '' });
                }}
                className="p-1 hover:bg-green-100 rounded"
                title="Edit category"
              >
                <Edit2 className="w-3 h-3 text-green-600" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCategory(category.category_id, category.name);
                }}
                className="p-1 hover:bg-red-100 rounded"
                title="Delete category"
              >
                <Trash2 className="w-3 h-3 text-red-600" />
              </button>
            </div>
          </div>
          
          {/* Inline Edit Form */}
          {editingCategory === category.category_id && (
            <div className="ml-8 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-2">
                <Input
                  value={editCategoryForm.name}
                  onChange={(e) => setEditCategoryForm({ ...editCategoryForm, name: e.target.value })}
                  placeholder="Category name"
                  className="text-sm"
                />
                <Textarea
                  value={editCategoryForm.description}
                  onChange={(e) => setEditCategoryForm({ ...editCategoryForm, description: e.target.value })}
                  placeholder="Description (optional)"
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleUpdateCategory}>
                    <Check className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setEditingCategory(null);
                      setEditCategoryForm({ name: '', description: '' });
                    }}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {isExpanded && renderCategoryTree(category.category_id, level + 1)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Forms Library</h2>
          <p className="text-sm text-slate-500">Manage California law forms for users</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setCategoryForm({ name: '', description: '', parent_id: null });
              setShowCategoryForm(true);
            }}
            variant="outline"
            className="rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
          <Button
            onClick={() => setShowFormUpload(true)}
            className="rounded-xl bg-violet-600 hover:bg-violet-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Form
          </Button>
        </div>
      </div>

      {/* Categories Tree */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Categories</h3>
        {categories.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No categories yet. Create one to get started.</p>
        ) : (
          <div className="space-y-1">
            {renderCategoryTree()}
          </div>
        )}
      </div>

      {/* Forms List */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">
            Forms ({filteredForms.length})
            {selectedCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className="ml-2 text-xs text-violet-600 hover:text-violet-700"
              >
                <X className="w-3 h-3 mr-1" />
                Clear filter
              </Button>
            )}
          </h3>
        </div>
        {filteredForms.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">
            {selectedCategory ? 'No forms in this category' : 'No forms uploaded yet.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredForms.map(form => (
              <div key={form.form_id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 group">
                <FileText className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{form.form_name}</div>
                  <div className="text-xs text-slate-500">
                    {form.form_code} • {form.category_name} • {form.total_fields} fields
                    {form.updated_at && (
                      <> • Updated {new Date(form.updated_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => handleDeleteForm(form.form_id, form.form_name)}
                  className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-50 rounded-lg transition-opacity"
                  title="Delete form"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add Category</h3>
            <div className="space-y-4">
              <div>
                <Label>Category Name *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g., Dissolution Forms"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Brief description"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreateCategory} className="flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  Create
                </Button>
                <Button variant="outline" onClick={() => setShowCategoryForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Upload Modal */}
      {showFormUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Upload California Law Form</h3>
            <div className="space-y-4">
              <div>
                <Label>Form Name *</Label>
                <Input
                  value={formData.form_name}
                  onChange={(e) => setFormData({ ...formData, form_name: e.target.value })}
                  placeholder="e.g., Petition (FL-100)"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Form Code *</Label>
                <Input
                  value={formData.form_code}
                  onChange={(e) => setFormData({ ...formData, form_code: e.target.value })}
                  placeholder="e.g., FL-100"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>PDF File *</Label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormFile(e.target.files[0])}
                  className="mt-1 w-full text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">Upload fillable PDF form (California law forms)</p>
              </div>
              
              {/* Strip XFA Option */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <input
                  type="checkbox"
                  id="strip-xfa-upload"
                  checked={stripXfa}
                  onChange={(e) => setStripXfa(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="strip-xfa-upload" className="flex-1 cursor-pointer">
                  <p className="text-xs font-medium text-blue-900">Strip XFA Security</p>
                  <p className="text-xs text-blue-700">
                    Removes XFA layer for Adobe Acrobat compatibility. Recommended for CA law forms.
                  </p>
                </label>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={handleUploadForm} 
                  disabled={uploadingForm}
                  className="flex-1"
                >
                  {uploadingForm ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload & Auto-Map</>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowFormUpload(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
